import { Button } from '@/components/Button'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetHeaderDescription,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import styles from './ReturnResultsDialog.module.css'

export interface ReturnResultsYear {
  taxYear: string
  federalAmount?: number
  stateAmounts: { state: string; amount: number }[]
  netAmount: number
  sourceLabel: string | null
}

export interface ReturnResultsDialogProps {
  years: ReturnResultsYear[]
  states: string[]
  net: number
  showing: boolean
  onClose: () => void
  onEdit?: () => void
}

/**
 * The final return amounts per tax year, federal and state side by side.
 */
export function ReturnResultsDialog({
  years,
  states,
  net,
  showing,
  onClose,
  onEdit,
}: ReturnResultsDialogProps) {
  const columns = `70px repeat(${states.length + 2}, minmax(0, 1fr)) 120px`

  return (
    <ModalSheet
      showing={showing}
      onRequestClose={onClose}
      testId="return-results-dialog"
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Return results</ModalSheetTitle>
        <ModalSheetHeaderDescription>
          From {years.length} final {years.length === 1 ? 'return' : 'returns'},
          one per tax year
        </ModalSheetHeaderDescription>
      </ModalSheetHeader>
      <ModalSheetContent>
        <div className={styles.total}>
          <span className={styles.totalAmount}>{signed(net)}</span>
          <span className={styles.totalLabel}>
            net {net < 0 ? 'owed' : 'refund'} across {years.length} tax{' '}
            {years.length === 1 ? 'year' : 'years'}
          </span>
        </div>
        <div className={styles.table}>
          <div className={styles.head} style={{ gridTemplateColumns: columns }}>
            <span>Tax year</span>
            <span className={styles.num}>Federal</span>
            {states.map((state) => (
              <span key={state} className={styles.num}>
                {state}
              </span>
            ))}
            <span className={styles.num}>Net</span>
            <span className={styles.source}>Source</span>
          </div>
          {years.map((year) => (
            <div
              key={year.taxYear}
              className={styles.row}
              style={{ gridTemplateColumns: columns }}
              data-test={`return-results-year-${year.taxYear}`}
            >
              <span className={styles.year}>{year.taxYear}</span>
              <span className={styles.num}>
                {year.federalAmount === undefined
                  ? '—'
                  : signed(year.federalAmount)}
              </span>
              {states.map((state) => {
                const amount = year.stateAmounts.find(
                  (s) => s.state === state,
                )?.amount
                return (
                  <span key={state} className={styles.num}>
                    {amount === undefined ? '—' : signed(amount)}
                  </span>
                )
              })}
              <span className={styles.net}>{signed(year.netAmount)}</span>
              <span className={styles.source}>{year.sourceLabel}</span>
            </div>
          ))}
        </div>
        <p className={styles.legend}>
          Refund (+) · Owed (−). Manual rows were typed in by a Pro instead of
          extracted.
        </p>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          {onEdit && (
            <Button label="Edit amounts" variant="default" onPress={onEdit} />
          )}
          <Button label="Done" variant="primary" onPress={onClose} />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

function signed(amount: number) {
  const rounded = Math.round(amount * 100) / 100
  const formatted = Math.abs(rounded).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
  return rounded > 0
    ? `+${formatted}`
    : rounded < 0
      ? `−${formatted}`
      : formatted
}
